import { getSupabaseClient } from "./providers/supabase/supabase";

/**
 * Tenant-app telemetry emitter (decision #43). Sends only what the
 * allowlist permits - route names and feature keys, never record contents -
 * authenticated with the user's own session token (the services verify it
 * against this workspace's auth and derive who sent it; nothing is
 * trusted from this payload). Errors are scrubbed of emails/phones here
 * AND again at the edge. Batched, sendBeacon on unload, silent on failure:
 * telemetry must never break the app it observes.
 */

interface QueuedEvent {
  type: "login" | "page_view" | "feature_used" | "session_heartbeat";
  key?: string;
  occurredAt: string;
}

const FLUSH_MS = 15_000;
const HEARTBEAT_MS = 5 * 60_000;

/** First path segment → the feature key the adoption chart understands. */
const FEATURE_BY_PATH: Record<string, string> = {
  reports: "reports.builder",
  deals: "deals.board",
  contacts: "contacts.list",
  marketing: "marketing.campaigns",
  tasks: "tasks.calendar",
  inbox: "inbox",
};

const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const PHONE_RE = /\+?\d[\d\s().-]{7,}\d/g;
const scrub = (s: string) => s.replace(EMAIL_RE, "[email]").replace(PHONE_RE, "[phone]");

const fingerprintOf = (message: string, stack: string) => {
  const basis = `${message.slice(0, 120)}|${stack.split("\n")[1] ?? ""}`;
  let h = 0;
  for (let i = 0; i < basis.length; i++) h = (Math.imul(31, h) + basis.charCodeAt(i)) | 0;
  return `fp-${(h >>> 0).toString(36)}`;
};

let started = false;

export function initTelemetry(opts: { slug: string; servicesUrl: string }) {
  if (started || typeof window === "undefined") return;
  started = true;
  const base = opts.servicesUrl.replace(/\/+$/, "");
  const usageUrl = `${base}/t/${opts.slug}/usage`;
  const errorsUrl = `${base}/t/${opts.slug}/errors`;
  const queue: QueuedEvent[] = [];

  const token = async (): Promise<string | null> => {
    try {
      const { data } = await getSupabaseClient().auth.getSession();
      return data.session?.access_token ?? null;
    } catch {
      return null;
    }
  };

  const flush = async () => {
    if (queue.length === 0) return;
    const t = await token();
    if (!t) return; // signed out - keep queueing, sends resume on sign-in
    const events = queue.splice(0, queue.length);
    try {
      await fetch(usageUrl, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${t}` },
        body: JSON.stringify({ events }),
        keepalive: true,
      });
    } catch {
      // Dropped beacons are acceptable; re-queueing risks unbounded growth.
    }
  };

  const push = (e: QueuedEvent) => {
    queue.push(e);
    if (queue.length >= 20) void flush();
  };

  const pageView = () => {
    const path = window.location.hash.replace(/^#\/?/, "") || window.location.pathname.slice(1);
    const first = path.split("/")[0] || "dashboard";
    push({ type: "page_view", key: first.slice(0, 60), occurredAt: new Date().toISOString() });
    const feature = FEATURE_BY_PATH[first];
    if (feature) {
      push({ type: "feature_used", key: feature, occurredAt: new Date().toISOString() });
    }
  };

  // Route changes: the app is hash/history routed - watch both.
  let lastHref = window.location.href;
  const onNav = () => {
    if (window.location.href === lastHref) return;
    lastHref = window.location.href;
    pageView();
  };
  window.addEventListener("popstate", onNav);
  window.addEventListener("hashchange", onNav);
  const origPush = history.pushState.bind(history);
  history.pushState = (...args: Parameters<History["pushState"]>) => {
    origPush(...args);
    onNav();
  };
  pageView(); // the landing view

  // Sign-ins.
  try {
    getSupabaseClient().auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") {
        push({ type: "login", occurredAt: new Date().toISOString() });
      }
    });
  } catch {
    // no client yet (demo) - fine
  }

  // Heartbeat while the tab is actually visible.
  setInterval(() => {
    if (document.visibilityState === "visible") {
      push({ type: "session_heartbeat", occurredAt: new Date().toISOString() });
    }
  }, HEARTBEAT_MS);

  setInterval(() => void flush(), FLUSH_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flush();
  });

  // Errors → fingerprint-grouped triage in HQ, scrubbed before leaving.
  const reportError = (message: string, stack: string) => {
    void (async () => {
      const t = await token();
      if (!t) return;
      try {
        await fetch(errorsUrl, {
          method: "POST",
          headers: { "content-type": "application/json", authorization: `Bearer ${t}` },
          body: JSON.stringify({
            fingerprint: fingerprintOf(message, stack),
            message: scrub(message).slice(0, 500),
            stackHash: fingerprintOf(stack, ""),
            route: scrub(window.location.hash || window.location.pathname).slice(0, 200),
            appVersion: typeof __BUILD_ID__ === "string" ? __BUILD_ID__ : undefined,
            occurredAt: new Date().toISOString(),
          }),
          keepalive: true,
        });
      } catch {
        // never let error reporting cause errors
      }
    })();
  };
  window.addEventListener("error", (e) => {
    if (e.message) reportError(String(e.message), String(e.error?.stack ?? ""));
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    reportError(
      String(reason?.message ?? reason ?? "unhandled rejection"),
      String(reason?.stack ?? ""),
    );
  });
}
